/**
 * GestioneDatiSection — Sezione "Gestione Dati" nella TenantDetailPage
 *
 * Flusso UX:
 * 1. Se nessun session token attivo → mostra pulsante "Avvia verifica identità"
 * 2. Clic → carica 6 domande dal server → form risposte
 * 3. Submit → se approvato → session token (1h) in stato locale + filtri export
 * 4. Se session token valido → filtri diretti senza re-verifica
 * 5. Genera export → download diretto o invio email
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Shield, ShieldCheck, ShieldOff, Download, Mail,
  AlertTriangle, CheckCircle, XCircle, Loader2,
  ChevronRight, Database, Clock, Info
} from 'lucide-react'
import { adminApi } from '../services/api'

// ============================================================
// Componente principale
// ============================================================
export default function GestioneDatiSection({ tenant }) {
  const tenantId = tenant._id

  // Step: 'idle' | 'loading_questions' | 'answering' | 'submitting' | 'export_ready' | 'generating'
  const [step, setStep] = useState('idle')
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [verifyError, setVerifyError] = useState(null)
  const [verifyResult, setVerifyResult] = useState(null) // { score, questions }

  // Session token (1h) per evitare re-verifica
  const [sessionToken, setSessionToken] = useState(null)
  const [sessionExpiry, setSessionExpiry] = useState(null)

  // Filtri export
  const [minoreId, setMinoreId] = useState('')
  const [educatoreId, setEducatoreId] = useState('')
  const [filterType, setFilterType] = useState('none') // 'none' | 'range' | 'year'
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo]     = useState('')
  const [year, setYear]         = useState(new Date().getFullYear().toString())
  const [dataTypes, setDataTypes] = useState(['anagrafica', 'presenze', 'note', 'pep'])
  const [format, setFormat]     = useState('json')
  const [deliveryType, setDeliveryType] = useState('download') // 'download' | 'email'
  const [sendToEmail, setSendToEmail] = useState('')

  // Feedback export
  const [exportError, setExportError] = useState(null)
  const [exportSuccess, setExportSuccess] = useState(null)

  // Elenchi per dropdown (lazy-loaded)
  const [minori, setMinori]     = useState([])
  const [educatori, setEducatori] = useState([])

  // Verifica sessione attiva
  const sessionActive = sessionToken && sessionExpiry && new Date() < new Date(sessionExpiry)

  useEffect(() => {
    if (step === 'export_ready') {
      loadDropdowns()
    }
  }, [step])

  // Se il formato è CSV, de-seleziona note e pep automaticamente
  useEffect(() => {
    if (format === 'csv') {
      setDataTypes(prev => prev.filter(t => !['note', 'pep'].includes(t)))
    }
  }, [format])

  // Blocca CSV se sono selezionati note/pep
  const csvBlocked = dataTypes.some(t => ['note', 'pep'].includes(t))

  const loadDropdowns = async () => {
    try {
      const [minoriRes, usersRes] = await Promise.allSettled([
        adminApi.getTenant(tenantId).then(r => r.data?.data?.minori || []),
        adminApi.getTenantUsers(tenantId).then(r =>
          (r.data?.data?.users || []).filter(u => u.role === 'educatore')
        )
      ])
      if (minoriRes.status === 'fulfilled') setMinori(minoriRes.value)
      if (usersRes.status === 'fulfilled')  setEducatori(usersRes.value)
    } catch {}
  }

  // ─── Passo 1: carica domande ────────────────────────────────
  const handleStartVerification = async () => {
    setStep('loading_questions')
    setVerifyError(null)
    try {
      const res = await adminApi.startExportVerification(tenantId)
      setQuestions(res.data.data.questions)
      const initialAnswers = {}
      res.data.data.questions.forEach(q => { initialAnswers[q.questionId] = '' })
      setAnswers(initialAnswers)
      setStep('answering')
    } catch (e) {
      setVerifyError(e.response?.data?.message || 'Errore nel caricamento delle domande')
      setStep('idle')
    }
  }

  // ─── Passo 2: submit risposte ───────────────────────────────
  const handleSubmitVerification = async () => {
    setStep('submitting')
    setVerifyError(null)

    const answersArray = Object.entries(answers).map(([questionId, providedAnswer]) => ({
      questionId,
      providedAnswer
    }))

    try {
      const res = await adminApi.submitExportVerification(tenantId, answersArray, {})
      const data = res.data

      if (!data.success) {
        setVerifyResult(data.data || null)
        setVerifyError(data.message || 'Verifica non superata')
        setStep('answering')
        return
      }

      // Estrae session token dalla risposta JSON
      const token = data.data?.sessionToken || res.headers?.['x-export-session-token'] || null
      if (token) {
        setSessionToken(token)
        setSessionExpiry(data.data?.sessionExpiresAt || new Date(Date.now() + 60 * 60 * 1000).toISOString())
      }

      setStep('export_ready')
    } catch (e) {
      const errData = e.response?.data
      if (errData && !errData.success) {
        setVerifyResult(errData.data || null)
        setVerifyError(errData.message || 'Verifica non superata')
      } else {
        setVerifyError(e.response?.data?.message || 'Errore nella verifica')
      }
      setStep('answering')
    }
  }

  // ─── Genera export ─────────────────────────────────────────
  const handleGenerateExport = async () => {
    setStep('generating')
    setExportError(null)
    setExportSuccess(null)

    const exportFilters = {
      minoreId:    minoreId    || undefined,
      educatoreId: educatoreId || undefined,
      dateFrom:    filterType === 'range' && dateFrom ? dateFrom : undefined,
      dateTo:      filterType === 'range' && dateTo   ? dateTo   : undefined,
      year:        filterType === 'year'  && year     ? parseInt(year) : undefined,
      dataTypes,
      format,
      sendToEmail: deliveryType === 'email' && sendToEmail ? sendToEmail : undefined
    }

    try {
      let res
      if (sessionActive) {
        res = await adminApi.generateExportWithToken(tenantId, sessionToken, exportFilters)
      } else {
        // Non dovrebbe accadere, ma per sicurezza
        setExportError('Sessione scaduta. Ripeti la verifica identità.')
        setStep('export_ready')
        return
      }

      // Controlla se la risposta è JSON (blob che contiene testo JSON)
      const contentType = res.headers?.['content-type'] || ''
      const isJson = contentType.includes('application/json')

      if (isJson) {
        // Leggi il blob come testo JSON
        const text = await res.data.text?.() || ''
        const json = text ? JSON.parse(text) : {}
        setExportSuccess(json.message || 'Export completato')
        setStep('export_ready')
        return
      }

      // Blob download
      const contentDisposition = res.headers?.['content-disposition'] || ''
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      const fileName = fileNameMatch?.[1] || `export_${tenant.slug || tenantId}.${format}`

      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)

      setExportSuccess(`File "${fileName}" scaricato con successo.`)
    } catch (e) {
      setExportError(e.response?.data?.message || 'Errore nella generazione dell\'export')
    } finally {
      if (step === 'generating') setStep('export_ready')
    }
  }

  const toggleDataType = (type) => {
    setDataTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    )
  }

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center mb-5">
        <Database className="h-5 w-5 text-indigo-600 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900">Gestione Dati</h2>
        <span className="ml-auto text-xs text-gray-400">Richiede verifica identità (≥5/6)</span>
      </div>

      {/* ── Sessione attiva badge ── */}
      {sessionActive && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm">
          <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
          <span className="text-green-700 font-medium">Sessione attiva</span>
          <span className="text-green-500 ml-auto">
            valida fino alle {new Date(sessionExpiry).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      {/* ════════════════════════════════════════
          STEP: IDLE — nessuna verifica attiva
      ════════════════════════════════════════ */}
      {(step === 'idle' || step === 'loading_questions') && !sessionActive && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-50 rounded-full mb-4">
            <Shield className="h-7 w-7 text-indigo-600" />
          </div>
          <p className="text-gray-600 mb-2 font-medium">Export dati auditato</p>
          <p className="text-sm text-gray-400 mb-6 max-w-sm mx-auto">
            Per generare un export è richiesta la verifica identità (6 domande sul centro, soglia 5/6).
            La sessione è valida 1 ora.
          </p>
          {verifyError && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {verifyError}
            </div>
          )}
          <button
            onClick={handleStartVerification}
            disabled={step === 'loading_questions'}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
          >
            {step === 'loading_questions' ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento domande...</>
            ) : (
              <><Shield className="h-4 w-4" /> Avvia verifica identità</>
            )}
          </button>
        </div>
      )}

      {/* ════════════════════════════════════════
          STEP: ANSWERING — form 6 domande
      ════════════════════════════════════════ */}
      {(step === 'answering' || step === 'submitting') && (
        <div>
          <div className="flex items-center gap-2 mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <Info className="h-4 w-4 flex-shrink-0" />
            Rispondi alle domande seguenti riguardo il centro <strong>{tenant.name}</strong>.
            Soglia minima: 5 risposte corrette su 6.
          </div>

          {/* Esito precedente (se sotto soglia) */}
          {verifyResult && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-2">
                Verifica non superata — punteggio: {verifyResult.score?.effectiveScore?.toFixed(1)}/6
              </p>
              <div className="space-y-1">
                {verifyResult.questions?.map(q => (
                  <div key={q.questionId} className="flex items-center gap-2 text-xs">
                    {q.result === 'correct'   && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                    {q.result === 'partial'   && <CheckCircle className="h-3.5 w-3.5 text-yellow-500" />}
                    {q.result === 'incorrect' && <XCircle     className="h-3.5 w-3.5 text-red-400" />}
                    <span className="text-gray-600">{q.questionLabel}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4 mb-6">
            {questions.map((q, i) => (
              <div key={q.questionId}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {i + 1}. {q.questionLabel}
                  <span className="ml-2 text-xs text-gray-400 font-normal">{q.hint || ''}</span>
                </label>
                {q.options ? (
                  <select
                    value={answers[q.questionId] || ''}
                    onChange={e => setAnswers(prev => ({ ...prev, [q.questionId]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">— Seleziona —</option>
                    {q.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={answers[q.questionId] || ''}
                    onChange={e => setAnswers(prev => ({ ...prev, [q.questionId]: e.target.value }))}
                    placeholder={q.questionId === 'data_registrazione' ? 'gg/mm/aaaa' : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>
            ))}
          </div>

          {verifyError && !verifyResult && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {verifyError}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => { setStep('idle'); setVerifyError(null); setVerifyResult(null) }}
              disabled={step === 'submitting'}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
            >
              Annulla
            </button>
            <button
              onClick={handleSubmitVerification}
              disabled={step === 'submitting' || Object.values(answers).some(v => !v.trim())}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50"
            >
              {step === 'submitting' ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Verifica in corso...</>
              ) : (
                <><ChevronRight className="h-4 w-4" /> Conferma risposte</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          STEP: EXPORT_READY — filtri combinabili
      ════════════════════════════════════════ */}
      {(step === 'export_ready' || step === 'generating') && (
        <div className="space-y-6">

          {/* ── CHI ── */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-xs font-semibold text-gray-500 px-2 uppercase tracking-wider">CHI (opzionale)</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minore specifico</label>
                <select
                  value={minoreId}
                  onChange={e => setMinoreId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Tutti i minori</option>
                  {minori.map(m => (
                    <option key={m._id} value={m._id}>{m.cognome} {m.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Educatore specifico</label>
                <select
                  value={educatoreId}
                  onChange={e => setEducatoreId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Tutti gli educatori</option>
                  {educatori.map(e => (
                    <option key={e._id} value={e._id}>{e.cognome} {e.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          {/* ── QUANDO ── */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-xs font-semibold text-gray-500 px-2 uppercase tracking-wider">QUANDO (opzionale)</legend>
            <div className="flex gap-4 mt-3 mb-3">
              {['none', 'range', 'year'].map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="filterType"
                    value={t}
                    checked={filterType === t}
                    onChange={() => setFilterType(t)}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700">
                    {t === 'none' ? 'Tutto il periodo' : t === 'range' ? 'Range personalizzato' : 'Anno solare'}
                  </span>
                </label>
              ))}
            </div>
            {filterType === 'range' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Da</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">A</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}
            {filterType === 'year' && (
              <select value={year} onChange={e => setYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}
          </fieldset>

          {/* ── COSA ── */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-xs font-semibold text-gray-500 px-2 uppercase tracking-wider">COSA</legend>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              {[
                { id: 'anagrafica', label: 'Anagrafica minori' },
                { id: 'presenze',   label: 'Presenze' },
                { id: 'note',       label: 'Note diario' },
                { id: 'pep',        label: 'PEP' }
              ].map(({ id, label }) => (
                <label key={id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataTypes.includes(id)}
                    onChange={() => toggleDataType(id)}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* ── FORMATO ── */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-xs font-semibold text-gray-500 px-2 uppercase tracking-wider">FORMATO</legend>
            <div className="flex gap-4 mt-3">
              {[
                { val: 'json', label: 'JSON',     desc: 'Migrazione/importazione' },
                { val: 'pdf',  label: 'PDF',      desc: 'Uso legale, GDPR Art.15' },
                { val: 'csv',  label: 'CSV',      desc: 'Solo presenze + anagrafica', disabled: csvBlocked }
              ].map(({ val, label, desc, disabled }) => (
                <label key={val}
                  className={`flex-1 flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    disabled
                      ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                      : format === val
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <input type="radio" name="format" value={val}
                    disabled={disabled}
                    checked={format === val}
                    onChange={() => !disabled && setFormat(val)}
                    className="sr-only" />
                  <span className="font-semibold text-gray-800">{label}</span>
                  <span className="text-xs text-gray-500 text-center mt-0.5">{desc}</span>
                </label>
              ))}
            </div>
            {csvBlocked && format !== 'csv' && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                CSV disabilitato: "Note diario" o "PEP" contengono dati Art. 9 GDPR
              </p>
            )}
          </fieldset>

          {/* ── CONSEGNA ── */}
          <fieldset className="border border-gray-200 rounded-lg p-4">
            <legend className="text-xs font-semibold text-gray-500 px-2 uppercase tracking-wider">CONSEGNA</legend>
            <div className="flex gap-6 mt-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delivery" value="download" checked={deliveryType === 'download'}
                  onChange={() => setDeliveryType('download')} className="accent-indigo-600" />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <Download className="h-4 w-4" /> Scarica direttamente
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delivery" value="email" checked={deliveryType === 'email'}
                  onChange={() => setDeliveryType('email')} className="accent-indigo-600" />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <Mail className="h-4 w-4" /> Invia via email
                </span>
              </label>
            </div>
            {deliveryType === 'email' && (
              <input
                type="email"
                value={sendToEmail}
                onChange={e => setSendToEmail(e.target.value)}
                placeholder="email@destinatario.it"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            )}
          </fieldset>

          {/* Feedback */}
          {exportError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {exportError}
            </div>
          )}
          {exportSuccess && (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {exportSuccess}
            </div>
          )}

          {/* Avviso dataset grande */}
          {deliveryType === 'download' && (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              Dataset &gt;1000 registrazioni: l'export verrà inviato automaticamente via email anziché scaricato.
            </p>
          )}

          {/* CTA */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setStep('idle'); setSessionToken(null); setSessionExpiry(null) }}
              className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
            >
              <ShieldOff className="h-4 w-4 inline mr-1" />
              Chiudi sessione
            </button>
            <button
              onClick={handleGenerateExport}
              disabled={step === 'generating' || dataTypes.length === 0 || (deliveryType === 'email' && !sendToEmail)}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
            >
              {step === 'generating' ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generazione in corso...</>
              ) : (
                <><Download className="h-4 w-4" /> Genera Export</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
